/**
 * Custom hook that contains all the logic from the loadData useEffect. 
 * It is be responsible for fetching, parsing, and merging all the initial data (layout, globalStyles, pages, etc.) 
 * and returns a single initialData object along with an isLoading flag.
 */
import { useState, useEffect } from 'react';
import listService from "ListService";
import sequenceService from 'SequenceService';
import util from "util/Util.js";
import mobileConstants from '../../../mobile/commons/MobileFormsConstants';
import mobileListService from 'mobile/MobileListService';

import {getSectionStyleProps} from '../utils/styleUtils';
import {
    ensurePxUnit, processControlModel, normalizeAttributes, createDefaultSectionObject,
    processPageModelAttributes, processAppModelAttributes, syncMarginsWithGlobalSpacing

} from '../utils/mobileUtils';

export const useWizardLoader = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [initialData, setInitialData] = useState(null);

    useEffect(() => {

        try {
            const hasExistingMobileData = listService.mobileSections?.size > 0 || listService.mobileObjects?.size > 0;
            const globalStyles = buildGlobalStylesFromListService(hasExistingMobileData);
            const data = hasExistingMobileData 
            ? buildInitialStateFromExistingListService(globalStyles) 
            : buildInitialStateForNewApp(globalStyles);
            setInitialData(data);
        } catch (error) {
            console.error("Failed to build initial data from listService:", error);
            setInitialData({
                layout: {}, globalStyles: mobileConstants.DEFAULT_WIZARD_STYLES,
                pages: [], appName: '', appAttributes: {}, initialObjectsSnapshot: {}
            });
        } finally {
            setIsLoading(false);
        }
    }, []);

    return { isLoading, initialData };
};

const buildInitialStateForNewApp = () => {
    
    const layout = {};
    const pages = [];
    const objects = [];
    const sections = [];
    const columns = [];
    const appModel = listService.currentForm.model;

    listService.pageList.forEach((desktopPageView) => {
        const pageId = desktopPageView.id;
        const pageName = desktopPageView.model.get('pageTitle') || desktopPageView.model.get('name');
        const pageAttributes = processPageModelAttributes(appModel, desktopPageView.model);

        pages.push({
            id: pageId,
            name: pageName,
            ...pageAttributes
        });

        // Create default columns for this page
        const defaultColumn = { id: sequenceService.nextDomId(), name: 'Column', order: 0, pageId: pageId };
        const col1 = { id: sequenceService.nextDomId(), name: 'Column 1', order: 1, pageId: pageId };
        const col2 = { id: sequenceService.nextDomId(), name: 'Column 2', order: 2, pageId: pageId };
        columns.push(defaultColumn, col1, col2);

        // Create default sections
        const newSectionId = sequenceService.nextDomId();
        const defaultSection = createDefaultSectionObject(
            newSectionId,
            'New Section',
            pageId,
            defaultColumn.id,
            1
        );
        sections.push(defaultSection);
        layout[pageId] = [defaultSection];
    });

    const globalSpacing = globalStyles?.marginSpacing || 8;
    const globalDefaultType = globalStyles?.defaultMargin || 'bottom';
    const defaultMargins = syncMarginsWithGlobalSpacing(globalDefaultType, globalSpacing);

    const initializeControlState = (controlData, pageId) => {
        const initializedControl = {
            ...controlData,
            pageId: pageId,
            isHidden: true,
            sectionId: undefined,
            attributes: {}, 
            margins: { ...defaultMargins }, 
            order: -1,
            isNew: true, 
            height: ensurePxUnit(controlData.height),
            width: ensurePxUnit(controlData.width),
        };

        if (Array.isArray(controlData.children) && controlData.children.length > 0) {
            initializedControl.children = controlData.children.map(child => 
                initializeControlState(child, pageId)
            );
        }

        return initializedControl;
    };

    listService.objectList.forEach((control) => {
        if (control.id !== 'aForm' && layout.hasOwnProperty(control.pageId)) {
            const baseControlData = processControlModel(control.model);
            const fullControlData = initializeControlState(baseControlData, control.pageId);
            objects.push(fullControlData);
        }
    });

    const flattenControls = (arr) => arr.flatMap(c => [c, ...(c.children ? flattenControls(c.children) : [])]);
    const allControlsFlat = flattenControls(objects);

    const globalStyles = JSON.parse(JSON.stringify(mobileConstants.DEFAULT_WIZARD_STYLES));
   
    const initialObjectsSnapshot = allControlsFlat.reduce((map, obj) => {
        map[obj.id] = { id: obj.id, attributes: { ...(obj.attributes || {}) }, isNew: obj.isNew };
        return map;
    }, {});


    const appAttributes = buildAppAttributesFromListService();
    const appName = listService.currentForm.model.get('name');

    return {
        layout,
        pages,
        globalStyles,
        initialObjectsSnapshot,
        appName,
        appAttributes,
    };
};

const buildInitialStateFromExistingListService = (globalStyles) => {
    
    const layout = buildLayoutFromListService(globalStyles);
    const pages = buildAppPageDataFromListService();
    const appAttributes = buildAppAttributesFromListService();
    const appName = listService.currentForm.model.get('name');
    const initialObjectsSnapshot = createSnapshotFromListService();

    return {
        layout, pages, globalStyles, initialObjectsSnapshot, appName, appAttributes,
    };
};

//Builds the initial 'layout' state object by reading from listService._mobileSections and listService._mobileObjects.
const buildLayoutFromListService = (globalStyles) => {
    const layout = {};
    const mobileObjectsMap = listService.mobileObjects?.toMap() || new Map();
    const desktopObjectsMap = listService.objectList?.toMap() || new Map();
    const mobileSections = listService.mobileSections();
    const pageSource = listService.pageList;

    // Initialize layout for all relevant pages
    pageSource.forEach(page => {
        const pageId = page?.id || page?.model?.id;
        if (pageId) {
            layout[pageId] = [];
        }
    });

    const globalSpacingSize = globalStyles.marginSpacing || 8;
    const globaldefaultMargin = globalStyles.defaultMargin || 'bottom';

    // If mobile sections exist, populate the layout from them
    if (mobileSections && typeof mobileSections.sortBy === 'function') {
        mobileSections
            .sortBy(s => s.index || 0)
            .forEach(sectionData => {
                const section = typeof sectionData.toObject === 'function' ? sectionData.toObject() : { ...sectionData };
                const pageId = section.pageId;

                if (layout[pageId]) {
                    const controlsForSection = mobileObjectsMap
                         .filter(obj => 
                                 obj?.sectionId === section.id && 
                                 !obj?.isHidden && 
                                 !obj?.groupId)
                        .sort((a, b) => (a?.order || 0) - (b?.order || 0))
                        .map(mobileObjData => {
                            if (!mobileObjData) return null;
                            const mobileData = typeof mobileObjData.toObject === 'function' ? mobileObjData.toObject() : { ...mobileObjData };
                            const controlId = mobileData.id;

                            const desktopControlView = desktopObjectsMap.get(controlId);
                            const baseControlData = processControlModel(desktopControlView.model);
                            const mobileOverrideAttributes = normalizeAttributes(mobileData || {});

                            const finalAttributes = {
                                ...mobileOverrideAttributes,
                                ...baseControlData.attributes
                            };

                            let finalMargins = syncMarginsWithGlobalSpacing(mobileData.margins, globaldefaultMargin, globalSpacingSize);
                            

                            return {
                                ...baseControlData,
                                id: controlId,
                                pageId: pageId,
                                sectionId: section.id,
                                isHidden: false,
                                groupId: baseControlData.groupId || mobileData.groupId,
                                order: mobileData.order,
                                margins: finalMargins,
                                isNew: mobileData.isNew || false,
                                height: ensurePxUnit(mobileData.height || baseControlData.height),
                                width: ensurePxUnit(mobileData.width || baseControlData.width),

                                attributes: finalAttributes,
                            };
                        })
                        .filter(Boolean)
                        .valueSeq()
                        .toArray();

                    const mobileSectionView = mobileListService.getMobileControl(section.id);
                    const mobileSectionModel = mobileSectionView ? mobileSectionView.model : null;

                    const mobileModelOverrideAttributes = mobileSectionModel
                        ? getSectionStyleProps(mobileSectionModel)
                        : {};

                    const finalSectionAttributes = {
                        ...(section.attributes || {}), // Attributes saved by wizard (or loaded initially)
                        ...mobileModelOverrideAttributes // Overrides from legacy designer model
                    };

                    layout[pageId].push({
                        ...section,
                        attributes: finalSectionAttributes,
                        controls: controlsForSection,
                    });
                } else {
                    console.warn(`Section ${section.id} belongs to page ${pageId}, which is not in the current page list. Skipping.`);
                }
            });

        }

    return layout;
};


//Builds the initial 'globalStyles' state object
const buildGlobalStylesFromListService = (hasExistingData = false) => {
    const defaultStyles = JSON.parse(JSON.stringify(mobileConstants.DEFAULT_WIZARD_STYLES));

    if (!hasExistingData) {
        return defaultStyles;
    }

    const savedFontFamily = listService.getMobileSetting('fontFamily');
    const margins = listService.getMobileStyleByKey('control_margin') || {};
    const savedMarginSpacing = margins.marginSize;
    const defaultMargin = margins.defaultMargin;
    const savedMaxAppWidth = listService.currentForm?.model?.get('maxAppWidth');

    defaultStyles.fontFamily = savedFontFamily || 'Poppins';
    defaultStyles.defaultMargin = defaultMargin || 'bottom';
    defaultStyles.marginSpacing = parseInt(savedMarginSpacing || 8, 10);
    defaultStyles.maxAppWidth = parseInt(savedMaxAppWidth || 650, 10);

    const savedStylesArray = listService.getMobileStyles();
   
    savedStylesArray.forEach((savedStyle, key) => {
        const targetStyle = defaultStyles.fontStyling?.[key];

        if (targetStyle) {
            const { fontSize, fontWeight, fontStyle, textAlign, show } = savedStyle;

            if (fontSize !== undefined) targetStyle.fontSize = fontSize;
            if (fontWeight !== undefined) targetStyle.fontWeight = fontWeight;
            if (fontStyle !== undefined) targetStyle.fontStyle = fontStyle;
            if (textAlign !== undefined) targetStyle.textAlign = textAlign;
            if (show !== undefined) targetStyle.show = util.isTrue(show);
        }
    });

    return defaultStyles;
};


// Builds the initial 'pages' array from listService._mobilePages + desktop info.
const buildAppPageDataFromListService = () => {
    const appModel = listService.currentForm.model;
    const appPagesData = [];
    const pageSource = listService.pageList;

    pageSource.forEach(pageData => {
        const pageId = pageData?.id || pageData?.model?.id;
        const pageName = pageData?.name || pageData?.model?.get('pageTitle');

        if (!pageId) return;

        const desktopPage = listService.getPage(pageId);
        const pageModel = desktopPage?.model;

        if (!pageModel) {
            console.warn(`Desktop page model not found for ID: ${pageId}. Skipping page.`);
            return;
        }

        const pageAttributes = processPageModelAttributes(appModel, pageModel);

        appPagesData.push({
            name: pageName,
            id: pageId,
            ...pageAttributes
        });
    });
    return appPagesData;
};


//Builds the app attributes (background, frame) from the main form model.
const buildAppAttributesFromListService = () => {
    const appModel = listService.currentForm.model;
    const attributes = processAppModelAttributes(appModel);

    return attributes;
};


// Creates the initial object snapshot from listService._mobileObjects
const createSnapshotFromListService = () => {
    const snapshot = {};
    listService.mobileObjects?.forEach(obj => {
        if (obj) {
            snapshot[obj.id] = { id: obj.id, attributes: { ...(obj.attributes || {}) }, isNew: obj.isNew };
        }
    });
    return snapshot;
};